import { Outlet, useNavigate, NavLink, useLocation } from 'react-router-dom'
import { useAuthStore, useNotificationStore } from '@store'
import { useState, useRef, useEffect } from 'react'
import avatarPlaceholder from '@assets/images/image_01.png'
import { ROUTES } from '@constants'
import Footer from '@components/common/Footer'
import { conversationService } from '@services/api/messages'
import { notificationService } from '@services/api/notifications'
import { storageURL, mediaUrl } from '@config'
import publicApiClient from '@services/api/publicClient'
import { bindUserChannel } from '@services/pusher'
import { encodeId } from '@utils/idCipher'

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.kudhi.app'
const APP_STORE_URL  = 'https://apps.apple.com/app/kudhi/id000000000'

function getStoreUrl() {
  const ua = navigator.userAgent
  return /iPhone|iPad|iPod|Mac/i.test(ua) ? APP_STORE_URL : PLAY_STORE_URL
}

// Icône SVG + couleurs selon le type de notification
function renderNotifIcon(type) {
  const config = {
    like: {
      bg: 'bg-red-100', text: 'text-red-500',
      path: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
    },
    comment: {
      bg: 'bg-blue-100', text: 'text-blue-500',
      path: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
    },
    new_post: {
      bg: 'bg-yellow-100', text: 'text-[#D4AF37]',
      path: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
    },
  }
  const c = config[type] || {
    bg: 'bg-gray-100', text: 'text-gray-500',
    path: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
  }
  return (
    <div className={`w-10 h-10 ${c.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
      <svg className={`w-5 h-5 ${c.text}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d={c.path} />
      </svg>
    </div>
  )
}

// Formate une date en temps relatif (maintenant, 27min, 2h, 3j, 04 juin)
function formatRelativeTime(d) {
  if (!d) return ''
  const date = new Date(d)
  if (isNaN(date)) return ''
  const diff = Date.now() - date
  const m = Math.floor(diff / 60000)
  const h = Math.floor(diff / 3600000)
  const dy = Math.floor(diff / 86400000)
  if (m < 1) return 'maintenant'
  if (m < 60) return `${m}min`
  if (h < 24) return `${h}h`
  if (dy < 7) return `${dy}j`
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}

function DashboardLayout() {
  const { user, logout, userType } = useAuthStore()
  const { unreadCount, messagesUnreadCount, setMessagesUnreadCount, addNotification } = useNotificationStore()
  const navigate = useNavigate()
  const location = useLocation()
  const isFullscreen = ['/messages', '/notifications'].includes(location.pathname)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Recherche dynamique
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)
  const searchRef = useRef(null)

  // Debounce recherche
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      setShowSearchDropdown(false)
      return
    }
    const timer = setTimeout(async () => {
      try {
        setSearchLoading(true)
        const res = await publicApiClient.get('/search', { params: { q: searchQuery, limit: 6 } })
        setSearchResults(res.data || [])
        setShowSearchDropdown(true)
      } catch {
        setSearchResults([])
      } finally {
        setSearchLoading(false)
      }
    }, 350)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Fermer le dropdown si clic extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const getResultRoute = (item) => {
    if (item.type === 1) return `/video/${encodeId(item.post_id)}`
    if (item.type === 3) return `/banner/${encodeId(item.post_id)}`
    return `/product/${encodeId(item.post_id)}`
  }
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [showMessages, setShowMessages] = useState(false)
  const [selectedLang, setSelectedLang] = useState('fr')
  const [recentMessages, setRecentMessages] = useState([])
  const [recentNotifications, setRecentNotifications] = useState([])
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [loadingNotifications, setLoadingNotifications] = useState(false)
  const [notificationsUnreadCount, setNotificationsUnreadCount] = useState(0)
  const dropdownRef = useRef(null)
  const langDropdownRef = useRef(null)
  const notificationsRef = useRef(null)
  const messagesRef = useRef(null)

  // Charger les données depuis l'API (messages/notifications)
  useEffect(() => {
    if (!user) return

    const loadData = async () => {
      // Charger les messages
      try {
        setLoadingMessages(true)
        const response = await conversationService.getConversations()
        if (response.success) {
          const convs = response.data || []
          const totalUnread = convs.reduce((sum, conv) => sum + (conv.unread_count || 0), 0)
          setMessagesUnreadCount(totalUnread)
          setRecentMessages(convs.slice(0, 3).map((c) => ({
            id: c.id,
            contact_full_name: [c.other_client?.nom, c.other_client?.prenom].filter(Boolean).join(' ') || 'Inconnu',
            contact_avatar: c.other_client?.photo_profil
              ? (c.other_client.photo_profil.startsWith('http') ? c.other_client.photo_profil : mediaUrl(c.other_client.photo_profil))
              : null,
            last_message: c.last_message?.urlArticle
              ? (c.last_message?.nomArticle || 'Article partagé')
              : (c.last_message?.message || 'Aucun message'),
            timestamp: c.last_message?.created_at ?? c.updated_at,
            unread_count: c.unread_count || 0,
          })))
        }
      } catch (error) {
        console.error('Erreur chargement messages:', error)
      } finally {
        setLoadingMessages(false)
      }

      // Charger les notifications
      try {
        setLoadingNotifications(true)
        const response = await notificationService.getNotifications()
        if (response.success) {
          const unreadNotifs = response.data.filter(n => !n.is_read)
          setNotificationsUnreadCount(unreadNotifs.length)
          setRecentNotifications(response.data.slice(0, 5))
        }
      } catch (error) {
        console.error('Erreur chargement notifications:', error)
      } finally {
        setLoadingNotifications(false)
      }
    }

    loadData()

    // Messages en temps réel : le badge se met à jour instantanément à chaque
    // nouveau message via le canal user-{id} (plus besoin de poller les messages).
    let cleanupPusher = () => {}
    if (user?.client_id) {
      cleanupPusher = bindUserChannel(user.client_id, {
        onConversationUpdated: () => loadData(),
      })
    }

    // Les notifications ne sont pas diffusées via Pusher → on garde un rafraîchissement
    // périodique (plus espacé) comme repli.
    const interval = setInterval(loadData, 30000)

    const handleReloadNotifications = () => loadData()
    window.addEventListener('reloadNotifications', handleReloadNotifications)

    return () => {
      clearInterval(interval)
      cleanupPusher()
      window.removeEventListener('reloadNotifications', handleReloadNotifications)
    }
  }, [user])

  // Fermer les dropdowns si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setIsLangDropdownOpen(false)
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false)
      }
      if (messagesRef.current && !messagesRef.current.contains(event.target)) {
        setShowMessages(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    await logout()
    navigate(ROUTES.LOGIN)
  }

  // Nom d'affichage compatible admin (name) et client (nom/prenom)
  const displayName = user?.name || [user?.nom, user?.prenom].filter(Boolean).join(' ') || 'Utilisateur'
  const displayInitials = [
    (user?.nom || user?.name || '').charAt(0),
    (user?.prenom || '').charAt(0),
  ].filter(Boolean).join('').toUpperCase() || 'U'
  const rawPhoto = user?.photo_profil || user?.photo || null
  const displayPhoto = rawPhoto
    ? (rawPhoto.startsWith('http') ? rawPhoto
      : rawPhoto.startsWith('/storage/') ? `${storageURL.replace(/\/storage$/, '')}${rawPhoto}`
      : mediaUrl(rawPhoto))
    : null

  return (
    <div className="min-h-screen bg-[#fefcf8]">
      {/* Header avec barre de recherche */}
      <header className="bg-[#fefcf8] border-b border-gray-200 sticky top-0 z-50">
        <div className="w-full px-3 sm:px-4 md:w-[90%] lg:w-[80%] md:mx-auto py-2 md:py-3">
          <div className="flex items-center justify-between gap-2 md:gap-4">
            {/* Logo */}
            <button
              onClick={() => navigate(ROUTES.HOME)}
              className="hover:opacity-80 transition-opacity flex-shrink-0"
            >
              <img
                src="/Logo_LIGDI.png"
                alt="LIGDI"
                className="h-14 sm:h-14 md:h-14 w-auto object-contain"
              />
            </button>

            {/* Navigation + Recherche + Icons — droite */}
            <div className="flex items-center gap-2 sm:gap-3">

              {/* Burger mobile */}
              <button
                className="lg:hidden w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center bg-yellow-100 hover:bg-yellow-200 rounded-full transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <svg className="w-6 h-6 text-[#001F3F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen
                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  }
                </svg>
              </button>

              {/* Navigation desktop */}
              <nav className="hidden lg:flex items-center gap-5">
                {[
                  { to: ROUTES.HOME, label: 'KUDHI', end: true },
                  { to: ROUTES.PRICING, label: 'Tarification' },
                ].map(({ to, label, end }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    className={({ isActive }) =>
                      isActive
                        ? 'text-[#D4AF37] font-bold border-b-2 border-[#D4AF37] pb-0.5 transition-colors whitespace-nowrap text-base'
                        : 'text-[#001F3F] hover:text-[#D4AF37] font-medium transition-colors whitespace-nowrap text-base'
                    }
                  >
                    {label}
                  </NavLink>
                ))}
                <NavLink
                  to={ROUTES.LIGDI}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 font-medium transition-colors whitespace-nowrap text-base ${isActive ? 'text-[#D4AF37] font-bold border-b-2 border-[#D4AF37] pb-0.5' : 'text-[#001F3F] hover:text-[#D4AF37]'}`
                  }
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                  KUDHI Market
                </NavLink>
                <a href={getStoreUrl()} target="_blank" rel="noopener noreferrer" className="text-[#001F3F] font-medium hover:text-[#D4AF37] transition-colors whitespace-nowrap text-base">
                  Téléchargez l'APK
                </a>
              </nav>

              {/* Barre de recherche - Desktop */}
              <div className="hidden md:flex w-56 lg:w-72 relative" ref={searchRef}>
                <div className="relative w-full">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchQuery.trim() && setShowSearchDropdown(true)}
                    placeholder="Recherchez ici !"
                    className="w-full px-3 py-2 pr-9 rounded-lg bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                  />
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                    {searchLoading
                      ? <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      : <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    }
                  </div>
                </div>

                {/* Dropdown résultats */}
                {showSearchDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-80 overflow-y-auto">
                    {searchResults.length === 0 ? (
                      <p className="px-4 py-3 text-sm text-gray-500 text-center">Aucun résultat pour « {searchQuery} »</p>
                    ) : (
                      <>
                        {searchResults.map((item) => (
                          <button
                            key={item.post_id}
                            onClick={() => {
                              navigate(getResultRoute(item))
                              setShowSearchDropdown(false)
                              setSearchQuery('')
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left border-b border-gray-100 last:border-0"
                          >
                            {/* Miniature */}
                            {item.first_image?.chemin ? (
                              <img
                                src={mediaUrl(item.first_image.chemin)}
                                alt=""
                                className="w-10 h-10 rounded object-cover flex-shrink-0"
                                onError={(e) => { e.target.style.display = 'none' }}
                              />
                            ) : (
                              <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{item.libele}</p>
                              <p className="text-xs text-gray-500">
                                {item.type === 1 ? 'Vidéo' : item.type === 3 ? 'Bannière' : 'Article'}
                                {item.prix ? ` · ${parseFloat(item.prix).toLocaleString('fr-FR')} FCFA` : ''}
                              </p>
                            </div>
                          </button>
                        ))}
                        <div className="px-3 py-2 bg-gray-50 text-center">
                          <p className="text-xs text-gray-500">{searchResults.length} résultat(s) trouvé(s)</p>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Mon Espace */}
              <NavLink
                to={ROUTES.DASHBOARD}
                className="hidden lg:flex items-center gap-1.5 px-4 py-2 bg-[#D4AF37] hover:bg-[#c09d2a] text-white font-semibold rounded-lg transition-colors whitespace-nowrap text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Mon Espace
              </NavLink>

              {/* Bouton recherche - Mobile */}
              <button
                onClick={() => setShowSearch(true)}
                className="hidden w-9 h-9 sm:w-11 sm:h-11 items-center justify-center bg-yellow-100 hover:bg-yellow-200 rounded-full transition-colors"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-darkBlue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              {/* Messages */}
              <div className="relative" ref={messagesRef}>
                <button 
                  onClick={() => setShowMessages(!showMessages)}
                  className="relative w-10 h-10 sm:w-11 sm:h-11 md:w-13 md:h-13 flex items-center justify-center bg-yellow-100 hover:bg-yellow-200 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-darkBlue" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                  {messagesUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 sm:top-0 sm:right-0 md:top-1 md:right-1 w-4 h-4 sm:w-5 sm:h-5 bg-red-600 text-white text-[10px] sm:text-xs rounded-full flex items-center justify-center font-medium">
                      {messagesUnreadCount > 99 ? '99+' : messagesUnreadCount}
                    </span>
                  )}
                </button>

                {/* Modal Messages */}
                {showMessages && (
                  <div className="absolute right-0 mt-2 w-72 sm:w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                      <h3 className="font-bold text-lg text-darkBlue">Messages</h3>
                      <button 
                        onClick={() => {
                          navigate('/messages')
                          setShowMessages(false)
                        }}
                        className="text-primary text-sm hover:underline"
                      >
                        Tout voir
                      </button>
                    </div>
                    <div className="overflow-y-auto max-h-80">
                      {loadingMessages ? (
                        <div className="p-8 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                      ) : recentMessages.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <p className="text-sm">Aucun message récent</p>
                        </div>
                      ) : (
                        recentMessages.map((conv) => {
                          const avatar = conv.contact_avatar
                          const avatarUrl = avatar 
                            ? (avatar.startsWith('http') ? avatar : `http://localhost/ligdi/public/${avatar}`)
                            : avatarPlaceholder
                          
                          return (
                            <button 
                              key={conv.id} 
                              onClick={() => {
                                navigate('/messages')
                                setShowMessages(false)
                              }}
                              className="w-full p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 text-left"
                            >
                              <div className="flex items-start gap-3">
                                <img src={avatarUrl} alt={conv.contact_full_name} className="w-10 h-10 rounded-full object-cover" />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="font-semibold text-sm text-darkBlue truncate">{conv.contact_full_name}</span>
                                    <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{formatRelativeTime(conv.timestamp) || 'Récent'}</span>
                                  </div>
                                  <p className="text-sm text-gray-600 line-clamp-2">{conv.last_message || 'Aucun message'}</p>
                                  {conv.unread_count > 0 && (
                                    <span className="inline-block mt-1 px-2 py-0.5 bg-red-600 text-white text-xs rounded-full">
                                      {conv.unread_count} nouveau{conv.unread_count > 1 ? 'x' : ''}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </button>
                          )
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Notifications */}

              <div className="relative" ref={notificationsRef}>
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative w-10 h-10 sm:w-11 sm:h-11 md:w-13 md:h-13 flex items-center justify-center bg-yellow-100 hover:bg-yellow-200 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-darkBlue" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                  </svg>
                  {notificationsUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 sm:top-0 sm:right-0 md:top-1 md:right-1 w-4 h-4 sm:w-5 sm:h-5 bg-red-600 text-white text-[10px] sm:text-xs rounded-full flex items-center justify-center font-medium">
                      {notificationsUnreadCount > 99 ? '99+' : notificationsUnreadCount}
                    </span>
                  )}
                </button>

                {/* Modal Notifications */}

                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-72 sm:w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                      <h3 className="font-bold text-lg text-darkBlue">Notifications</h3>
                      <button 
                        onClick={() => {
                          navigate('/notifications')
                          setShowNotifications(false)
                        }}
                        className="text-primary text-sm hover:underline"
                      >
                        Tout voir
                      </button>
                    </div>
                    <div className="overflow-y-auto max-h-80">
                      {loadingNotifications ? (
                        <div className="p-8 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                      ) : recentNotifications.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <p className="text-sm">Aucune notification</p>
                        </div>
                      ) : (
                        recentNotifications.map((notif) => (
                          <button 
                            key={notif.id} 
                            onClick={() => {
                              navigate('/notifications')
                              setShowNotifications(false)
                            }}
                            className={`w-full p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 text-left ${
                              !notif.is_read ? 'bg-blue-50' : ''
                            }`}
                          >
                          <div className="flex items-start gap-3">
                            {renderNotifIcon(notif.type)}
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-semibold text-darkBlue mb-1">{notif.title}</h4>
                              <p className="text-xs text-gray-600 mb-1 line-clamp-2">{notif.message}</p>
                              <span className="text-xs text-gray-400">{formatRelativeTime(notif.timestamp || notif.created_at)}</span>
                              {!notif.is_read && (
                                <span className="ml-2 inline-block w-2 h-2 bg-blue-600 rounded-full"></span>
                              )}
                            </div>
                          </div>
                        </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Avatar avec dropdown */}

              <div className="relative" ref={dropdownRef}>
                <button 
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="hover:opacity-80 transition-opacity"
                >
                  {displayPhoto ? (
                    <img
                      src={displayPhoto}
                      alt=""
                      className="w-11 h-11 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full object-cover border-2 border-primary"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }}
                    />
                  ) : null}
                  <div
                    className="w-11 h-11 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-primary to-yellow-500 items-center justify-center border-2 border-primary select-none"
                    style={{ display: displayPhoto ? 'none' : 'flex' }}
                  >
                    <span className="text-white text-sm sm:text-base font-bold tracking-wide">
                      {displayInitials}
                    </span>
                  </div>
                </button>

                {/* Dropdown menu */}

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    {/* User info header */}
                    <div className="px-4 py-3 border-b border-gray-200">
                      <p className="text-sm font-semibold text-darkBlue truncate">{displayName}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
                    </div>
                    <button
                      onClick={() => {
                        navigate(ROUTES.DASHBOARD)
                        setIsDropdownOpen(false)
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-darkBlue hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
                      </svg>
                      Mon Dashboard
                    </button>
                    <button
                      onClick={() => {
                        navigate(ROUTES.PROFILE)
                        setIsDropdownOpen(false)
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-darkBlue hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Mon Profil
                    </button>
                    <button
                      onClick={() => {
                        navigate('/settings')
                        setIsDropdownOpen(false)
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-darkBlue hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Paramètres
                    </button>
                    <button
                      onClick={() => {
                        navigate('/help')
                        setIsDropdownOpen(false)
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-darkBlue hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Aide
                    </button>
                    <hr className="my-2 border-gray-200" />
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Déconnexion
                    </button>
                  </div>
                )}
              </div>

              {/* Sélecteur de langue avec dropdown */}
              <div className="relative hidden sm:block" ref={langDropdownRef}>
                {/* <button 
                  onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <span className="text-sm sm:text-base font-semibold text-darkBlue">{selectedLang === 'fr' ? 'FR' : 'EN'}</span>
                  <span className="text-xs sm:text-sm text-gray-500 hidden md:inline">{selectedLang === 'fr' ? 'Fr' : 'En'}</span>
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 text-darkBlue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button> */}

                {/* Dropdown menu langues */}
                {/* {isLangDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <button
                      onClick={() => {
                        setSelectedLang('fr')
                        setIsLangDropdownOpen(false)
                      }}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 transition-colors ${
                        selectedLang === 'fr' ? 'bg-gray-50 text-darkBlue font-semibold' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-semibold">FR</span>
                      <span>Français</span>
                    </button>
                    <button
                      onClick={() => {
                        setSelectedLang('en')
                        setIsLangDropdownOpen(false)
                      }}
                      className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 transition-colors ${
                        selectedLang === 'en' ? 'bg-gray-50 text-darkBlue font-semibold' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-semibold">GB</span>
                      <span>English</span>
                    </button>
                  </div>
                )} */}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Menu mobile */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-b border-gray-200 shadow-lg z-40">
          <nav className="flex flex-col divide-y divide-gray-100">
            {[
              { to: ROUTES.HOME, label: 'KUDHI', end: true },
              { to: ROUTES.PRICING, label: 'Tarification' },
              { to: ROUTES.LIGDI, label: 'KUDHI Market' },
            ].map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `px-6 py-4 text-base font-medium transition-colors ${isActive ? 'text-[#D4AF37] bg-yellow-50' : 'text-[#001F3F] hover:bg-gray-50'}`
                }
              >
                {label}
              </NavLink>
            ))}
            <a
              href={getStoreUrl()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileMenuOpen(false)}
              className="px-6 py-4 text-base font-medium text-[#001F3F] hover:bg-gray-50 transition-colors"
            >
              Téléchargez l'APK
            </a>
            <NavLink
              to={ROUTES.DASHBOARD}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `px-6 py-4 text-base font-medium transition-colors ${isActive ? 'text-[#D4AF37] bg-yellow-50' : 'text-[#001F3F] hover:bg-gray-50'}`
              }
            >
              Mon Espace
            </NavLink>
            <button
              onClick={() => { handleLogout(); setMobileMenuOpen(false) }}
              className="px-6 py-4 text-base font-medium text-red-600 hover:bg-red-50 transition-colors text-left"
            >
              Déconnexion
            </button>
          </nav>
        </div>
      )}

      {/* Barre de recherche étendue - Mobile */}

      {showSearch && (
        <div className="bg-white border-b border-gray-200 sticky top-[65px] z-40 md:hidden">
          <div className="flex items-center gap-3 p-4">
            <button 
              onClick={() => setShowSearch(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
            >
              <svg className="w-6 h-6 text-darkBlue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Recherchez ici !"
                autoFocus
                className="w-full px-4 py-2.5 pr-12 rounded-full bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
              />
              <button className="absolute right-3 top-1/2 -translate-y-1/2">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <main>
        <Outlet />
      </main>

      {!isFullscreen && <Footer />}
    </div>
  )
}

export default DashboardLayout
